import * as d3 from 'd3';
import { darkTheme, lightTheme } from '../theme';
import { useTheme as useSiteTheme } from '../../theme/ThemeContext';

const fmtPrice = d3.format('$,.2f');
const fmtVolume = d3.format(',.2s');
const fmtDate = d3.timeFormat('%B %-d, %Y');

// Fixed top-left OHLC legend -- shows the hovered bar (from crosshair
// onHover), falling back to the last real bar at rest. Replaces the old
// div-tooltip-pinned-near-the-corner (which didn't track the cursor) and
// professionalTradingChart.jsx's separate SVG-text tooltip implementation.
const OhlcLegendHeader = ({ symbol, bar, prevBar, markLabel }) => {
  const { theme: siteThemeMode } = useSiteTheme();
  const theme = siteThemeMode === 'light' ? lightTheme : darkTheme;
  if (!bar) return null;

  // Compared against the *previous bar's* close, matching the price tag's
  // own up/down color on the right axis (priceLabel.js) -- these used to
  // disagree (this legend compared the bar's own open-to-close instead),
  // which could show a green "+0.19%" here next to a red price tag for the
  // exact same bar.
  const basis = prevBar ? prevBar.close : bar.open;
  const change = bar.close - basis;
  const changePct = basis ? (change / basis) * 100 : 0;
  const isUp = change >= 0;
  const color = isUp ? theme.bull.strong : theme.bear.strong;

  return (
    <div
      // pr-16 keeps this clear of the price tag drawn inside the SVG at the
      // right edge (~66px wide) -- without it, the stats row (which never
      // had flex-wrap at all) ran straight under the tag on narrow screens
      // instead of wrapping, since this overlay has no awareness of the
      // SVG-drawn price tag's position.
      className="absolute top-2 left-2 right-0 z-10 pointer-events-none select-none flex flex-col gap-0.5 pr-16"
      style={{ fontFamily: theme.typography.fontFamily }}
    >
      <div className="flex items-center gap-2 flex-wrap text-[13px] leading-tight">
        <span className="font-semibold text-gray-900 dark:text-white">{symbol}</span>
        <span className="text-[11px] text-gray-400 dark:text-white/40">{markLabel}</span>
        <span className="text-gray-400 dark:text-white/40">{fmtDate(bar.date)}</span>
      </div>
      <div
        className="flex items-center gap-x-2.5 gap-y-0.5 flex-wrap text-[11px] sm:text-[12px] leading-tight"
        style={{ fontVariantNumeric: 'tabular-nums', color: theme.axis.text }}
      >
        <span>
          O<span className="ml-1 text-gray-700 dark:text-white/80">{fmtPrice(bar.open)}</span>
        </span>
        <span>
          H<span className="ml-1 text-gray-700 dark:text-white/80">{fmtPrice(bar.high)}</span>
        </span>
        <span>
          L<span className="ml-1 text-gray-700 dark:text-white/80">{fmtPrice(bar.low)}</span>
        </span>
        <span>
          C<span className="ml-1 text-gray-700 dark:text-white/80">{fmtPrice(bar.close)}</span>
        </span>
        <span style={{ color }}>
          {isUp ? '+' : ''}
          {fmtPrice(change)} ({isUp ? '+' : ''}
          {changePct.toFixed(2)}%)
        </span>
        <span className="text-gray-400 dark:text-white/40">Vol {fmtVolume(bar.volume).replace('G', 'B')}</span>
      </div>
    </div>
  );
};

export default OhlcLegendHeader;
